/**
 * Employee essentials — self-service modules granted in bulk via the
 * User Permission edit screen toggle. Keys must match UserPermissionEdit
 * module_name + subItem.key and backend JSONField names.
 */

export const ESSENTIALS_GRANT_FLAGS = {
    permission: true,
    active: true,
    edit: true,
    add: true,
    draft: false,
};

export const ESSENTIALS_REVOKE_FLAGS = {
    permission: false,
    active: false,
    edit: false,
    add: false,
    draft: false,
};

/** Core pack — always included when essentials is enabled. */
export const CORE_EMPLOYEE_ESSENTIALS = [
    { module: 'attendance', key: 'day_wise_attendance', label: 'Day Attendance' },
    { module: 'attendance', key: 'attendance_log', label: 'Attendance Log' },
    { module: 'attendance', key: 'shift_change_requests', label: 'Shift Change / Swap' },
    { module: 'employee', key: 'leave_request', label: 'Leave Request' },
    { module: 'employee', key: 'timesheet', label: 'My Tasks' },
    { module: 'employee', key: 'reimbursements_request', label: 'Reimbursement Request' },
    { module: 'employee', key: 'advance_salary_request', label: 'Advance Salary Request' },
    { module: 'employee', key: 'resignation', label: 'Resignation Request' },
];

/** Included only when the corresponding company flag is on. */
export const OPTIONAL_EMPLOYEE_ESSENTIALS = [
    {
        module: 'employee',
        key: 'overtime_request',
        label: 'Overtime Request',
        when: (ctx) => ctx.overtimeWorkflowEnabled,
    },
    {
        module: 'appraisal',
        key: 'my_appraisals',
        label: 'My Appraisals',
        when: () => true,
    },
    {
        module: 'training',
        key: 'my_trainings',
        label: 'My Trainings',
        when: (ctx) => ctx.nabhEnabled,
    },
];

export function getActiveEmployeeEssentials(ctx = {}) {
    const optional = OPTIONAL_EMPLOYEE_ESSENTIALS.filter(
        (item) => !item.when || item.when(ctx)
    );
    return [...CORE_EMPLOYEE_ESSENTIALS, ...optional];
}

export function isEssentialPermission(moduleName, subKey, essentials) {
    return essentials.some((item) => item.module === moduleName && item.key === subKey);
}

export function getEssentialsGrantState(permissions, essentials) {
    if (!permissions?.length || !essentials.length) return 'off';

    const grantedCount = essentials.filter(({ module, key }) => {
        const mod = permissions.find((p) => p.module_name === module);
        return !!mod?.module_list?.[key]?.permission;
    }).length;

    if (grantedCount === 0) return 'off';
    if (grantedCount === essentials.length) return 'on';
    return 'partial';
}

export function applyEssentialsToPermissions(permissions, essentials, enabled) {
    const flags = enabled ? ESSENTIALS_GRANT_FLAGS : ESSENTIALS_REVOKE_FLAGS;

    const nextPermissions = permissions.map((module) => {
        const essentialsForModule = essentials.filter((e) => e.module === module.module_name);
        if (!essentialsForModule.length) return module;

        const moduleList = { ...module.module_list };
        for (const { key } of essentialsForModule) {
            moduleList[key] = { ...flags };
        }
        return { ...module, module_list: moduleList };
    });

    // Home is required for every logged-in user.
    if (enabled) {
        return nextPermissions.map((module) => {
            if (module.module_name !== 'home') return module;
            return {
                ...module,
                module_list: {
                    ...module.module_list,
                    home: {
                        permission: true,
                        active: !!module.module_list?.home?.active,
                        edit: !!module.module_list?.home?.edit,
                        add: !!module.module_list?.home?.add,
                        draft: !!module.module_list?.home?.draft,
                    },
                },
            };
        });
    }

    return nextPermissions;
}
